import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("div", { class: "flex items-center gap-2" }, [
    /*#__PURE__*/_createElementVNode("h3", { class: "text-sm font-semibold text-gray-900" }, "Automatic phase progression"),
    /*#__PURE__*/_createElementVNode("span", { class: "rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-600" }, " Internal admin ")
  ]),
  /*#__PURE__*/_createElementVNode("p", { class: "mt-1 text-sm text-gray-500" }, " The default for every school in this group. When a learner masters every concept of every thread on their assessment level, they are moved to the next level without waiting for an admin. Individual schools can override this either way under Edit School. ")
], -1)
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = {
  key: 0,
  class: "mt-3 text-sm text-red-600"
}
const _hoisted_6 = {
  key: 1,
  class: "mt-3 text-sm text-green-600"
}

import { computed, ref, watch } from "vue";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import { useSchoolGroupStore } from "@/stores/schoolGroupsStore";
import { useFilterStore } from "@/stores/filterStore";
import type { SchoolGroup } from "@/interfaces/school-group.interface";
import type { SchoolGroupFilterOption } from "@/interfaces/filters.interface";
import { showToast } from "@/utils/toastUtils";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/vue/20/solid";

/**
 * Group-level automatic phase progression (NLM-Test #95).
 *
 * This is the switch a partner is actually enabled with: one write here covers
 * every school in the group. Modelled on SchoolGroupFreshchatSettingsCard,
 * with two differences that matter:
 *
 *   - it is internal-admin only, and the server strips the field for anyone
 *     else while still answering 200, so the card trusts the response rather
 *     than the value it asked for;
 *   - two states are enough here. A group has nothing above it to inherit
 *     from, so undefined and false both mean off. The three-state control
 *     belongs on the school (PhaseProgressionSettingsCard).
 */
type SchoolGroupLike = (SchoolGroup | SchoolGroupFilterOption) & {
  settings?: { autoPhaseProgressionEnabled?: boolean | null };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SchoolGroupPhaseProgressionSettingsCard',
  setup(__props) {

const schoolGroupStore = useSchoolGroupStore();
const filterStore = useFilterStore();

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const currentSchoolGroup = computed<SchoolGroupLike | null>(() => {
  if (schoolGroupStore.activeSchoolGroup) {
    return schoolGroupStore.activeSchoolGroup as SchoolGroupLike;
  }

  const selected = filterStore.selectedSchoolGroup;
  return selected ? (selected as SchoolGroupLike) : null;
});

// `=== true`: a group that predates the field reads back undefined, which is off.
const isEnabled = (group: SchoolGroupLike | null | undefined) =>
  group?.settings?.autoPhaseProgressionEnabled === true;

const localValue = ref(false);

watch(
  currentSchoolGroup,
  (group) => {
    localValue.value = isEnabled(group);
  },
  { immediate: true }
);

const onToggle = async (value: boolean) => {
  if (loading.value || !currentSchoolGroup.value) return;

  const groupId =
    currentSchoolGroup.value?._id || filterStore.selectedFilters.schoolGroup || "";

  if (!groupId) {
    errorMessage.value = "Select a school group to update automatic phase progression.";
    return;
  }

  const previousValue = localValue.value;
  localValue.value = value;
  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  const result = await schoolGroupStore.updateSchoolGroup(groupId, {
    settings: { autoPhaseProgressionEnabled: value }
  });

  loading.value = false;

  if (!result) {
    localValue.value = previousValue;
    errorMessage.value =
      "Unable to update automatic phase progression. Please try again.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon
    });
    return;
  }

  const saved = result as SchoolGroupLike;

  filterStore.updateSchoolGroupSettings(saved._id, saved.settings);

  // The server strips this field for non-internal admins and still answers
  // 200, so the saved document is the only trustworthy source.
  localValue.value = isEnabled(saved);

  if (localValue.value !== value) {
    errorMessage.value =
      "That change was not applied. Automatic phase progression can only be changed by an internal admin.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon
    });
    return;
  }

  successMessage.value = value
    ? "Automatic phase progression enabled for this school group."
    : "Automatic phase progression disabled for this school group.";
  showToast({
    text: successMessage.value,
    type: "success",
    icon: CheckCircleIcon
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_unref(SwitchGroup), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(SwitchLabel), { class: "text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(localValue.value ? 'Enabled' : 'Disabled'), 1)
              ]),
              _: 1
            }),
            _createVNode(_unref(Switch), {
              "model-value": localValue.value,
              disabled: loading.value || !currentSchoolGroup.value,
              "onUpdate:modelValue": onToggle,
              class: _normalizeClass([
              localValue.value ? 'bg-blue-600' : 'bg-gray-300',
              loading.value || !currentSchoolGroup.value
                ? 'opacity-60 cursor-not-allowed'
                : 'cursor-pointer',
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2'
            ])
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", {
                  "aria-hidden": "true",
                  class: _normalizeClass([
                localValue.value ? 'translate-x-6' : 'translate-x-1',
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform'
              ])
                }, null, 2)
              ]),
              _: 1
            }, 8, ["model-value", "disabled", "class"])
          ])
        ]),
        _: 1
      })
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(errorMessage.value), 1))
      : (successMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true)
  ]))
}
}

})